"use client";

import { useMemo, useState } from "react";
import { DocumentPreview } from "@/components/tool-kit/document-preview";
import {
  Panel,
  SelectField,
  TextAreaField,
  TextField,
} from "@/components/tool-kit/primitives";
import { BUTTON_SECONDARY } from "@/components/tool-kit/styles";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { tPlural } from "@/lib/i18n/interpolate";
import {
  buildLabReport,
  type LabReportInput,
} from "@/lib/tool-kit/essay-templates/lab-report";

/** English strings. A translated content file overrides these through `ui`. */
export const LAB_REPORT_GENERATOR_STRINGS = {
  tryExample: "Try an example",
  clear: "Clear the form",
  detailsHeading: "Report details",
  titleLabel: "Report title",
  titlePlaceholder: "e.g. The Effect of Temperature on Amylase Activity",
  bylineLabel: "Name, course, and date (optional)",
  bylinePlaceholder: "e.g. Sam Lee — CHEM 110, Section 2 — October 3, 2026",
  aimHeading: "Aim and hypothesis",
  aimLabel: "Aim",
  aimPlaceholder: "To determine how … affects …",
  hypothesisLabel: "Hypothesis",
  hypothesisPlaceholder: "If …, then … because …",
  variablesHeading: "Variables and results table",
  independentLabel: "Independent variable — what you changed, with units",
  independentPlaceholder: "e.g. Temperature (°C)",
  dependentLabel: "Dependent variable — what you measured, with units",
  dependentPlaceholder: "e.g. Time for the starch to break down (s)",
  controlledLabel: "Controlled variables — one per line",
  controlledPlaceholder: "e.g. Enzyme concentration\nVolume of starch solution",
  levelsLabel: "Values of the independent variable you tested (optional)",
  levelsPlaceholder: "e.g. 20, 30, 40, 50, 60",
  levelsHint:
    "Separate them with commas. They become the rows of your results table.",
  trialsLabel: "Trials per value",
  trialsOption: "one: trial|other: trials",
  methodHeading: "Materials and method",
  materialsLabel: "Materials and equipment — one per line",
  materialsPlaceholder: "e.g. 250 cm³ beaker\n1% amylase solution, 10 cm³",
  methodLabel: "Method — one step per line",
  methodPlaceholder:
    "e.g. Five water baths were set to 20, 30, 40, 50, and 60 °C.",
  resultsHeading: "Results",
  observationsLabel: "Results notes — one observation per line (optional)",
  observationsPlaceholder: "e.g. The starch broke down fastest at 40 °C.",
  fileName: "lab-report",
  document: {
    heading: "Your lab report template",
    prompt: "To write",
    guidance: "Tip",
    copy: "Copy",
    copied: "Copied!",
    downloadText: "Download .txt",
    downloadMarkdown: "Download .md",
    print: "Print",
    remaining: "one: part still to write|other: parts still to write",
    complete: "Every section has your content.",
  },
  defaultTitle: "Lab Report",
  sections: {
    abstract: "Abstract",
    introduction: "Introduction",
    methods: "Materials and Methods",
    results: "Results",
    discussion: "Discussion",
    conclusion: "Conclusion",
    references: "References",
  },
  subheadings: {
    variables: "Variables",
    controlled: "Controlled variables",
    materials: "Materials",
    procedure: "Procedure",
    analysis: "Data analysis",
  },
  fields: {
    aim: "Aim",
    hypothesis: "Hypothesis",
    independent: "Independent variable",
    dependent: "Dependent variable",
    hypothesisTested: "Hypothesis tested",
    aimToAnswer: "Aim to answer",
  },
  guidance: {
    abstract:
      "Write this section last. One paragraph, often 150–250 words — check your course's limit. Past tense for what you did and found, present tense for what it means.",
    introduction:
      "Present tense for established theory. Move from general background to your specific question, and end with the aim and hypothesis.",
    methods:
      "Past tense, with enough detail for someone else to repeat the experiment: quantities, concentrations, equipment sizes, and the number of trials. Most university reports want prose here rather than numbered steps — check which your course expects.",
    results:
      "Past tense, no interpretation — the why belongs in the Discussion. Number tables and figures in the order you mention them, refer to each one in the text, and give units in every column heading.",
    observations:
      "Turn these notes into sentences that point the reader to Table 1 and Figure 1:",
    figures:
      "Most science formats put a table's title above the table and a figure's caption below the figure; APA style puts both above. Follow your course's style, and put raw data or sample calculations in an appendix if they are required.",
    discussion:
      "Present tense to interpret, past tense to report your own results. Hedge your claims: one experiment supports or fails to support a hypothesis — it rarely proves one.",
    references:
      "List every source cited in the Introduction and Discussion — including the lab manual if you relied on it — in your course's style (often APA, ACS, or CSE).",
  },
  prompts: {
    title:
      "Give the report a specific title that names both variables, such as “The Effect of Light Intensity on the Rate of Photosynthesis in Elodea”.",
    abstract:
      "Summarize the aim, the method in a sentence or two, the key result with its numbers, and your conclusion.",
    background:
      "Explain the science the experiment depends on — key concepts, equations, or earlier findings — and cite your sources.",
    aim: "State the aim in one sentence: what you investigated, and in what system.",
    hypothesis:
      "Make a testable prediction with its reason: “If …, then … because ….”",
    independent:
      "Name the variable you changed, with its unit and the values you used.",
    dependent:
      "Name the variable you measured, how you measured it, and its unit.",
    controlled: "List what you kept the same, and how you kept it constant.",
    materials:
      "List every item with its size, quantity, or concentration, in SI units (e.g. 250 cm³ beaker, 2.0 g sodium chloride).",
    procedure:
      "Describe each step in the order you carried it out, including the number of trials.",
    analysis:
      "Explain how you processed the raw data: means, calculations (one worked example here or in an appendix), uncertainties, and any statistical test.",
    results:
      "Describe the main trend in words and give the key values — for example, the mean for each condition — pointing the reader to Table 1 and Figure 1.",
    figure:
      "Figure 1: plot {dependent} on the y-axis against {independent} on the x-axis. Label both axes with units, and add error bars if you calculated uncertainty.",
    trend:
      "Describe the trend and state whether the results support the hypothesis, quoting the key values.",
    theory: "Explain the trend using the theory from your Introduction.",
    errors:
      "Evaluate the method: name the main sources of systematic and random error, their likely effect on the results, and a specific improvement for each.",
    compare:
      "Compare your results with published values or other groups' data, and cite your sources.",
    conclusion:
      "In two to four sentences, answer the aim directly, say whether the hypothesis was supported, and give the key result with its number.",
    references:
      "Add your reference list here, ordered as your citation style requires.",
  },
  table: {
    number: "Table 1.",
    caption: "{dependent} by {independent}",
    captionGeneric:
      "Descriptive title: what was measured, under which conditions",
    independentHeader: "Independent variable (unit)",
    dependentHeader: "Dependent variable (unit)",
    trial: "Trial {n}",
    mean: "Mean",
  },
  fallback: {
    independent: "the independent variable",
    dependent: "the dependent variable",
  },
  example: {
    title:
      "The Effect of Light Intensity on the Rate of Photosynthesis in Elodea",
    byline: "Jordan Rivera — BIOL 101, Lab Section 4 — March 12, 2026",
    aim: "To determine how the distance between a lamp and a sprig of Elodea affects the rate of photosynthesis, measured as the rate of oxygen bubble production.",
    hypothesis:
      "If the lamp is moved closer to the Elodea, the rate of oxygen bubble production will increase, because light intensity limits the rate of the light-dependent reactions.",
    independent: "Distance from lamp (cm)",
    dependent: "Oxygen bubbles released per minute",
    controlled:
      "Water temperature (25 °C, checked with a thermometer)\nLength of the Elodea sprig (8 cm)\nConcentration of sodium bicarbonate solution (1%)\nThe same 10 W LED lamp throughout\nTwo minutes of adjustment time at each distance",
    levels: "10, 20, 30, 40, 50",
    materials:
      "Elodea sprig, 8 cm\nLarge test tube\n250 cm³ beaker of water\n1% sodium bicarbonate solution, 50 cm³\nDesk lamp with a 10 W LED bulb\nMeter stick\nThermometer\nStopwatch",
    method:
      "The Elodea sprig was placed, cut end up, in a large test tube of sodium bicarbonate solution.\nThe test tube was stood in a beaker of water at 25 °C to keep the temperature constant.\nThe lamp was positioned 10 cm from the test tube, and the sprig was left to adjust for two minutes.\nThe oxygen bubbles released from the cut end were counted for one minute.\nThe count was repeated twice more at the same distance.\nThe procedure was repeated with the lamp at 20, 30, 40, and 50 cm.",
    observations:
      "The bubble rate fell at every step as the lamp moved further away.\nThe mean rate was 38 bubbles per minute at 10 cm and 6 bubbles per minute at 50 cm.\nThe largest drop was between 10 cm and 20 cm.\nTrials at 50 cm varied the most (4 to 9 bubbles per minute).",
  },
} as const;

const EMPTY: LabReportInput = {
  title: "",
  byline: "",
  aim: "",
  hypothesis: "",
  independent: "",
  dependent: "",
  controlled: "",
  levels: "",
  trials: "3",
  materials: "",
  method: "",
  observations: "",
};

/** The example's choices that are settings, not prose — kept out of the translatable strings. */
const EXAMPLE_SETTINGS = { trials: "3" } as const;

const TRIAL_VALUES = ["1", "2", "3", "4", "5"] as const;

export default function LabReportGeneratorWidget() {
  const strings = useToolUi(LAB_REPORT_GENERATOR_STRINGS);
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const [input, setInput] = useState<LabReportInput>(EMPTY);
  const doc = useMemo(
    () => buildLabReport(input, strings, locale),
    [input, strings, locale]
  );
  const update = (key: keyof LabReportInput) => (value: string) =>
    setInput((previous) => ({ ...previous, [key]: value }));
  const trialOptions = TRIAL_VALUES.map((value) => {
    const count = Number(value);
    return {
      value,
      label: `${fmt(count)}${tPlural(strings.trialsOption, locale, count)}`,
    };
  });

  return (
    <div className="space-y-8">
      <div className="flex flex-wrap gap-3">
        <button
          className={BUTTON_SECONDARY}
          onClick={() =>
            setInput({ ...EMPTY, ...strings.example, ...EXAMPLE_SETTINGS })
          }
          type="button"
        >
          {strings.tryExample}
        </button>
        <button
          className={BUTTON_SECONDARY}
          onClick={() => setInput(EMPTY)}
          type="button"
        >
          {strings.clear}
        </button>
      </div>

      <Panel title={strings.detailsHeading}>
        <TextField
          id="lr-title"
          label={strings.titleLabel}
          onChange={update("title")}
          placeholder={strings.titlePlaceholder}
          value={input.title}
        />
        <TextField
          id="lr-byline"
          label={strings.bylineLabel}
          onChange={update("byline")}
          placeholder={strings.bylinePlaceholder}
          value={input.byline}
        />
      </Panel>

      <Panel title={strings.aimHeading}>
        <TextAreaField
          id="lr-aim"
          label={strings.aimLabel}
          onChange={update("aim")}
          placeholder={strings.aimPlaceholder}
          prose
          rows={2}
          value={input.aim}
        />
        <TextAreaField
          id="lr-hypothesis"
          label={strings.hypothesisLabel}
          onChange={update("hypothesis")}
          placeholder={strings.hypothesisPlaceholder}
          prose
          rows={2}
          value={input.hypothesis}
        />
      </Panel>

      <Panel title={strings.variablesHeading}>
        <div className="grid gap-4 sm:grid-cols-2">
          <TextField
            id="lr-independent"
            label={strings.independentLabel}
            onChange={update("independent")}
            placeholder={strings.independentPlaceholder}
            value={input.independent}
          />
          <TextField
            id="lr-dependent"
            label={strings.dependentLabel}
            onChange={update("dependent")}
            placeholder={strings.dependentPlaceholder}
            value={input.dependent}
          />
        </div>
        <TextAreaField
          id="lr-controlled"
          label={strings.controlledLabel}
          onChange={update("controlled")}
          placeholder={strings.controlledPlaceholder}
          prose
          rows={3}
          value={input.controlled}
        />
        <div className="grid gap-4 sm:grid-cols-[1fr_12rem]">
          <TextField
            hint={strings.levelsHint}
            id="lr-levels"
            label={strings.levelsLabel}
            onChange={update("levels")}
            placeholder={strings.levelsPlaceholder}
            value={input.levels}
          />
          <SelectField
            id="lr-trials"
            label={strings.trialsLabel}
            onChange={update("trials")}
            options={trialOptions}
            value={input.trials}
          />
        </div>
      </Panel>

      <Panel title={strings.methodHeading}>
        <TextAreaField
          id="lr-materials"
          label={strings.materialsLabel}
          onChange={update("materials")}
          placeholder={strings.materialsPlaceholder}
          prose
          rows={4}
          value={input.materials}
        />
        <TextAreaField
          id="lr-method"
          label={strings.methodLabel}
          onChange={update("method")}
          placeholder={strings.methodPlaceholder}
          prose
          rows={5}
          value={input.method}
        />
      </Panel>

      <Panel title={strings.resultsHeading}>
        <TextAreaField
          id="lr-observations"
          label={strings.observationsLabel}
          onChange={update("observations")}
          placeholder={strings.observationsPlaceholder}
          prose
          rows={3}
          value={input.observations}
        />
      </Panel>

      <DocumentPreview
        doc={doc}
        fileName={strings.fileName}
        idPrefix="lr"
        labels={strings.document}
      />
    </div>
  );
}
